import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, MagnifyingGlass, Funnel, PencilSimple, Trash, Handshake, Buildings, Star, Phone, EnvelopeSimple, Globe, Bank, Coin, Megaphone, ShareNetwork, Storefront, GraduationCap, Newspaper, DotsThreeCircle, FileText, NotePencil, Envelope, CheckCircle, ChatsCircle, PauseCircle, Archive, Medal, Minus, Clock, Tag } from '@phosphor-icons/react';
import { getPartners, createPartner, updatePartner, deletePartner, getCustomTags, createCustomTag } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import { DataTable } from '../components/DataTable';
import { CreatableSelect } from '../components/CreatableSelect';
import { CurrencyInput } from '../components/CurrencyInput';
import { PhoneInput } from '../components/PhoneInput';
import { TerritorySelect } from '../components/TerritorySelect';
import { findTerritoire } from '../constants/countries';
import { Flag } from '../components/Flag';
import { PageHero } from '../components/motion/PageHero';

const typePartenaireOptions = [
  { value: 'collectivite', label: 'Collectivité territoriale', Icon: Buildings, iconColor: '#2563EB' },
  { value: 'financeur', label: 'Financeur / Bailleur', Icon: Coin, iconColor: '#059669' },
  { value: 'prescripteur', label: 'Prescripteur', Icon: Megaphone, iconColor: '#B45309' },
  { value: 'reseau', label: 'Réseau professionnel', Icon: ShareNetwork, iconColor: '#2D5A4C' },
  { value: 'institution', label: 'Institution publique', Icon: Bank, iconColor: '#1E4136' },
  { value: 'chambre_consulaire', label: 'Chambre consulaire', Icon: Storefront, iconColor: '#7C3AED' },
  { value: 'organisme_formation', label: 'Organisme de formation', Icon: GraduationCap, iconColor: '#0284C7' },
  { value: 'banque', label: 'Banque / Finance solidaire', Icon: Bank, iconColor: '#0891B2' },
  { value: 'media', label: 'Média / Communication', Icon: Newspaper, iconColor: '#DB2777' },
  { value: 'autre', label: 'Autre', Icon: DotsThreeCircle, iconColor: '#6B7280' },
];

const typeConventionOptions = [
  { value: 'convention_cadre', label: 'Convention cadre', Icon: FileText, iconColor: '#2D5A4C' },
  { value: 'accord_partenariat', label: 'Accord de partenariat', Icon: Handshake, iconColor: '#059669' },
  { value: 'avenant', label: 'Avenant', Icon: NotePencil, iconColor: '#B45309' },
  { value: 'lettre_mission', label: 'Lettre de mission', Icon: Envelope, iconColor: '#2563EB' },
  { value: 'convention_subvention', label: 'Convention de subvention', Icon: Coin, iconColor: '#0891B2' },
  { value: 'autre', label: 'Autre', Icon: DotsThreeCircle, iconColor: '#6B7280' },
];

const statutOptions = [
  { value: 'actif', label: 'Actif', color: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle, iconColor: '#059669' },
  { value: 'en_discussion', label: 'En discussion', color: 'bg-blue-100 text-blue-800', Icon: ChatsCircle, iconColor: '#2563EB' },
  { value: 'suspendu', label: 'Suspendu', color: 'bg-orange-100 text-orange-800', Icon: PauseCircle, iconColor: '#EA580C' },
  { value: 'archive', label: 'Archivé', color: 'bg-gray-100 text-gray-800', Icon: Archive, iconColor: '#475569' },
];

const niveauRelationOptions = [
  { value: 'strategique', label: 'Stratégique', Icon: Star, iconColor: '#B45309' },
  { value: 'privilegiee', label: 'Privilégiée', Icon: Medal, iconColor: '#2D5A4C' },
  { value: 'standard', label: 'Standard', Icon: Minus, iconColor: '#6B7280' },
  { value: 'occasionnel', label: 'Occasionnel', Icon: Clock, iconColor: '#5E6D66' },
];

const emptyForm = {
  nom: '',
  type_partenaire: '',
  categorie: '',
  territoire: '',
  contact_principal: '',
  email: '',
  telephone: '',
  telephone_indicatif: '+596',
  adresse: '',
  ville: '',
  site_web: '',
  description: '',
  type_convention: '',
  date_debut_convention: '',
  date_fin_convention: '',
  montant_convention: '',
  statut: 'actif',
  niveau_relation: 'standard',
  tags: [],
};

export function PartnersPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [customTypes, setCustomTypes] = useState([]);       // type_partenaire personnalisés
  const [customConventions, setCustomConventions] = useState([]); // type_convention personnalisés

  const fetchCustomTags = async () => {
    try {
      const [tp, tc] = await Promise.all([getCustomTags('type_partenaire'), getCustomTags('type_convention')]);
      setCustomTypes(tp.data.items || []);
      setCustomConventions(tc.data.items || []);
    } catch {}
  };
  useEffect(() => { fetchCustomTags(); }, []);

  // Options fusionnées : défauts + tags personnalisés
  const mergedTypeOptions = [
    ...typePartenaireOptions,
    ...customTypes.map(t => ({ value: t.value, label: t.label, Icon: Tag, iconColor: '#2D5A4C', custom: true })),
  ];
  const mergedConventionOptions = [
    ...typeConventionOptions,
    ...customConventions.map(t => ({ value: t.value, label: t.label, Icon: Tag, iconColor: '#2D5A4C', custom: true })),
  ];
  const findTypePartenaire = (v) => mergedTypeOptions.find(t => t.value === v);
  const findTypeConvention = (v) => mergedConventionOptions.find(t => t.value === v);

  const handleCreateTypePartenaire = async (label) => {
    const res = await createCustomTag('type_partenaire', label);
    await fetchCustomTags();
    toast.success(`Type « ${label} » créé`);
    return res.data;
  };
  const handleCreateTypeConvention = async (label) => {
    const res = await createCustomTag('type_convention', label);
    await fetchCustomTags();
    toast.success(`Type de convention « ${label} » créé`);
    return res.data;
  };

  const fetchPartners = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType) params.type_partenaire = filterType;
      if (filterStatut) params.statut = filterStatut;
      const res = await getPartners(params);
      setPartners(res.data.items || []);
    } catch {
      toast.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, [search, filterType, filterStatut]);

  // Keyboard shortcut: 'n' opens new partner dialog
  useEffect(() => {
    const handleNew = () => { setEditingPartner(null); setFormData({ ...emptyForm }); setDialogOpen(true); };
    window.addEventListener('crm-shortcut-new', handleNew);
    return () => window.removeEventListener('crm-shortcut-new', handleNew);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.montant_convention) payload.montant_convention = parseFloat(payload.montant_convention);
      else delete payload.montant_convention;
      
      if (editingPartner) {
        await updatePartner(editingPartner.id, payload);
        toast.success('Partenaire mis a jour');
      } else {
        await createPartner(payload);
        toast.success('Partenaire cree');
      }
      setDialogOpen(false);
      setEditingPartner(null);
      setFormData({ ...emptyForm });
      fetchPartners();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur'));
    }
  };

  const handleEdit = (p) => {
    setEditingPartner(p);
    setFormData({
      nom: p.nom || '',
      type_partenaire: p.type_partenaire || '',
      categorie: p.categorie || '',
      territoire: p.territoire || '',
      contact_principal: p.contact_principal || '',
      email: p.email || '',
      telephone: p.telephone || '',
      telephone_indicatif: p.telephone_indicatif || '+596',
      adresse: p.adresse || '',
      ville: p.ville || '',
      site_web: p.site_web || '',
      description: p.description || '',
      type_convention: p.type_convention || '',
      date_debut_convention: p.date_debut_convention || '',
      date_fin_convention: p.date_fin_convention || '',
      montant_convention: p.montant_convention || '',
      statut: p.statut || 'actif',
      niveau_relation: p.niveau_relation || 'standard',
      tags: p.tags || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deletePartner(id);
      toast.success('Partenaire supprime');
      setDeleteConfirm(null);
      fetchPartners();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur de suppression'));
    }
  };

  const getStatutBadge = (statut) => {
    const s = statutOptions.find(o => o.value === statut);
    if (!s) return <Badge>{statut}</Badge>;
    const I = s.Icon;
    return <Badge className={`${s.color} gap-1.5`}><I size={12} weight="fill" style={{ color: s.iconColor }} />{s.label}</Badge>;
  };
  const getNiveauBadge = (niveau) => {
    const colors = { strategique: 'bg-[#2D5A4C] text-white', privilegiee: 'bg-blue-100 text-blue-800', standard: 'bg-gray-100 text-gray-800', occasionnel: 'bg-slate-50 text-slate-600' };
    const n = niveauRelationOptions.find(o => o.value === niveau);
    const I = n?.Icon;
    return (
      <Badge className={`${colors[niveau] || 'bg-gray-100 text-gray-800'} gap-1.5`}>
        {I && <I size={12} weight="fill" style={{ color: n.iconColor }} />}
        {n?.label || niveau}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Partenaires"
        subtitle="Écosystème coopératif — conventions et engagements"
        icon={<Handshake size={24} weight="duotone" className="text-white" />}
        gradient="from-[#80C51F] to-[#2D5A4C]"
        accents={['#00AEEF', '#FF8E00']}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="partners-title">
            Partenaires & Institutionnels
          </h1>
          <p className="text-[#5E6D66] mt-1">Relations avec collectivites, financeurs et reseaux</p>
        </div>
        <Button onClick={() => { setEditingPartner(null); setFormData({ ...emptyForm }); setDialogOpen(true); }} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="new-partner-btn">
          <Plus size={18} className="mr-2" />
          Nouveau partenaire
        </Button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg"><Handshake size={20} className="text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-actifs">{partners.filter(p => p.statut === 'actif').length}</p>
              <p className="text-xs text-[#5E6D66]">Actifs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-[#2D5A4C]/10 rounded-lg"><Star size={20} className="text-[#2D5A4C]" /></div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]" data-testid="stat-strategiques">{partners.filter(p => p.niveau_relation === 'strategique').length}</p>
              <p className="text-xs text-[#5E6D66]">Strategiques</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Buildings size={20} className="text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{partners.filter(p => p.type_partenaire === 'collectivite').length}</p>
              <p className="text-xs text-[#5E6D66]">Collectivites</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg"><Buildings size={20} className="text-yellow-600" /></div>
            <div>
              <p className="text-2xl font-bold text-[#1A2421]">{partners.filter(p => p.type_partenaire === 'financeur').length}</p>
              <p className="text-xs text-[#5E6D66]">Financeurs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input type="search" placeholder="Rechercher un partenaire..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 border-[#E8E6E1]" data-testid="partner-search" />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-[#E8E6E1]">
              <Funnel size={18} className="mr-2" />Filtres
            </Button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E8E6E1]">
              <Select value={filterType} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px] border-[#E8E6E1]"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {mergedTypeOptions.map(t => <SelectItem key={t.value} value={t.value}><span className="flex items-center gap-2"><t.Icon size={13} style={{ color: t.iconColor }} />{t.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatut} onValueChange={(v) => setFilterStatut(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {statutOptions.map(s => <SelectItem key={s.value} value={s.value}><span className="flex items-center gap-2"><s.Icon size={13} style={{ color: s.iconColor }} />{s.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>
          ) : partners.length === 0 ? (
            <div className="text-center py-12">
              <Handshake size={48} className="mx-auto text-[#E8E6E1] mb-4" />
              <p className="text-[#5E6D66]">Aucun partenaire trouve</p>
              <Button onClick={() => { setEditingPartner(null); setFormData({ ...emptyForm }); setDialogOpen(true); }} variant="link" className="text-[#2D5A4C] mt-2">Ajouter votre premier partenaire</Button>
            </div>
          ) : (
            <DataTable
              data={partners}
              columns={[
                {
                  key: 'nom',
                  label: 'Nom',
                  render: (val) => (
                    <div className="flex items-center gap-2">
                      <Handshake size={16} className="text-[#2D5A4C]" />
                      <span className="font-medium text-sm text-[#1A2421]">{val}</span>
                    </div>
                  ),
                },
                {
                  key: 'type_partenaire',
                  label: 'Type',
                  render: (val) => {
                    const t = findTypePartenaire(val);
                    const I = t?.Icon;
                    return (
                      <Badge variant="outline" className="text-xs gap-1">
                        {I && <I size={11} style={{ color: t.iconColor }} />}
                        {t?.label || val}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'territoire',
                  label: 'Territoire',
                  render: (val) => {
                    if (!val) return <span className="text-sm text-[#5E6D66]">-</span>;
                    const t = findTerritoire(val);
                    return (
                      <span className="flex items-center gap-1.5 text-sm text-[#1A2421]">
                        <Flag iso={t?.iso} size={14} />
                        {val}
                      </span>
                    );
                  },
                },
                {
                  key: 'contact_principal',
                  label: 'Contact',
                  render: (_, row) => (
                    <div className="text-sm">
                      {row.contact_principal && <p className="text-[#1A2421]">{row.contact_principal}</p>}
                      {row.email && <p className="text-[#5E6D66] text-xs flex items-center gap-1"><EnvelopeSimple size={12} />{row.email}</p>}
                      {row.telephone && <p className="text-[#5E6D66] text-xs flex items-center gap-1"><Phone size={12} />{row.telephone_indicatif ? `${row.telephone_indicatif} ` : ''}{row.telephone}</p>}
                    </div>
                  ),
                },
                {
                  key: 'niveau_relation',
                  label: 'Niveau',
                  render: (val) => getNiveauBadge(val),
                },
                {
                  key: 'statut',
                  label: 'Statut',
                  render: (val) => getStatutBadge(val),
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  className: 'text-right',
                  cellClassName: 'text-right',
                  render: (_, row) => (
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} data-testid={`edit-partner-${row.id}`}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(row)} className="text-[#D47A62] hover:text-[#D47A62]" data-testid={`delete-partner-${row.id}`}><Trash size={16} /></Button>
                    </div>
                  ),
                },
              ]}
              searchKeys={['nom', 'contact_principal', 'email', 'territoire']}
              showSearch={false}
              emptyMessage="Aucun partenaire trouve"
              data-testid="partners-datatable"
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>{editingPartner ? 'Modifier le partenaire' : 'Nouveau partenaire'}</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required data-testid="partner-nom-input" />
              </div>
              <div className="space-y-2">
                <Label>Type de partenaire *</Label>
                <CreatableSelect
                  value={formData.type_partenaire}
                  onChange={(v) => setFormData({ ...formData, type_partenaire: v })}
                  options={mergedTypeOptions}
                  placeholder="Sélectionner ou créer…"
                  onCreate={handleCreateTypePartenaire}
                  required
                  testId="partner-type-select"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Territoire</Label>
                <TerritorySelect
                  value={formData.territoire}
                  onChange={(v) => {
                    const t = findTerritoire(v);
                    setFormData(prev => ({
                      ...prev,
                      territoire: v,
                      telephone_indicatif: (!prev.telephone && t?.dial) ? t.dial : prev.telephone_indicatif,
                    }));
                  }}
                  testId="partner-territoire"
                  placeholder="Selectionner"
                />
              </div>
              <div className="space-y-2">
                <Label>Niveau de relation</Label>
                <Select value={formData.niveau_relation} onValueChange={(v) => setFormData({ ...formData, niveau_relation: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{niveauRelationOptions.map(n => <SelectItem key={n.value} value={n.value}><span className="flex items-center gap-2"><n.Icon size={13} style={{ color: n.iconColor }} />{n.label}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact principal</Label>
                <Input value={formData.contact_principal} onChange={(e) => setFormData({ ...formData, contact_principal: e.target.value })} placeholder="Nom du contact" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telephone</Label>
                <PhoneInput
                  indicatif={formData.telephone_indicatif}
                  number={formData.telephone}
                  onChange={({ indicatif, number }) => setFormData({ ...formData, telephone_indicatif: indicatif, telephone: number })}
                  testIdPrefix="partner-tel"
                />
              </div>
              <div className="space-y-2">
                <Label>Site web</Label>
                <Input value={formData.site_web} onChange={(e) => setFormData({ ...formData, site_web: e.target.value })} placeholder="https://..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} />
              </div>
            </div>

            <div className="border-t border-[#E8E6E1] pt-4">
              <p className="text-sm font-medium text-[#1A2421] mb-3">Convention / Partenariat</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Type de convention</Label>
                  <CreatableSelect
                    value={formData.type_convention}
                    onChange={(v) => setFormData({ ...formData, type_convention: v })}
                    options={mergedConventionOptions}
                    placeholder="Convention cadre…"
                    onCreate={handleCreateTypeConvention}
                    testId="partner-convention-select"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date debut</Label>
                  <Input type="date" value={formData.date_debut_convention} onChange={(e) => setFormData({ ...formData, date_debut_convention: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Input type="date" value={formData.date_fin_convention} onChange={(e) => setFormData({ ...formData, date_fin_convention: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Montant convention</Label>
                  <CurrencyInput
                    value={formData.montant_convention}
                    onChange={(n) => setFormData({ ...formData, montant_convention: n == null ? '' : n })}
                    currency="€"
                    testId="partner-montant-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select value={formData.statut} onValueChange={(v) => setFormData({ ...formData, statut: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{statutOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description / Notes</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Historique de la relation, objectifs..." />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-partner-btn">{editingPartner ? 'Mettre a jour' : 'Creer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[#5E6D66]">Voulez-vous vraiment supprimer le partenaire <strong>{deleteConfirm?.nom}</strong> ? Cette action est irreversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-partner">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
